import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { referralApi } from '../../api/referralApi'
import { ReferralApplication } from '../../types'
import { toast } from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import { ArrowLeft, FileText, Loader2, UserCheck } from 'lucide-react'
import { clsx } from 'clsx'

const STATUS_STYLE: Record<string, string> = {
  NEW:         'badge bg-yellow-50 text-yellow-700',
  SHORTLISTED: 'badge bg-blue-50 text-blue-700',
  REFERRED:    'badge bg-green-50 text-green-700',
  REJECTED:    'badge bg-red-50 text-red-700',
}

export default function JobApplicationsPage() {
  const { id } = useParams<{ id: string }>()
  const [applications, setApplications] = useState<ReferralApplication[]>([])
  const [loading, setLoading] = useState(true)
  const [referring, setReferring] = useState<number | null>(null)

  useEffect(() => {
    referralApi
      .getApplicationsForJob(Number(id))
      .then(({ data }) => setApplications((data.data as any)?.content || data.data || []))
      .catch(() => toast.error('Failed to load applications'))
      .finally(() => setLoading(false))
  }, [id])

  const handleRefer = async (applicationId: number) => {
    setReferring(applicationId)
    try {
      await referralApi.refer(applicationId)
      toast.success('Referral created! Escrow released to your wallet.')
      setApplications((prev) =>
        prev.map((a) => (a.id === applicationId ? { ...a, status: 'REFERRED' as const } : a))
      )
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create referral')
    } finally {
      setReferring(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/my-jobs" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 no-underline">
        <ArrowLeft size={16} /> Back to My Jobs
      </Link>

      <h1 className="text-xl font-bold text-gray-900 mb-6">Applications</h1>

      {applications.length === 0 ? (
        <div className="card text-center py-12 text-gray-400">No applications yet</div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <div key={app.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700">
                      {app.applicant.displayName?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{app.applicant.displayName}</p>
                    <p className="text-xs text-gray-400">
                      Applied {formatDistanceToNow(new Date(app.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                <span className={clsx(STATUS_STYLE[app.status])}>{app.status}</span>
              </div>

              {app.coverLetter && (
                <p className="text-sm text-gray-600 mt-3 line-clamp-3">{app.coverLetter}</p>
              )}

              <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-100">
                {app.resumeUrl && (
                  <a
                    href={app.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-xs flex items-center gap-1.5"
                  >
                    <FileText size={14} /> View Resume
                  </a>
                )}
                {app.status === 'NEW' && (
                  <button
                    onClick={() => handleRefer(app.id)}
                    disabled={referring === app.id}
                    className="btn-primary text-xs flex items-center gap-1.5"
                  >
                    {referring === app.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <UserCheck size={14} />
                    )}
                    Refer This Candidate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
